import { useMemo } from 'react'
import { useReadiness, type ReadinessCtx } from '@/lib/readiness/useReadiness'
import { useMigrationScope } from '@/stores/migrationStore'
import { useEnsureMigrationScope } from '@/lib/migration/usePlanSummary'
import { buildSummary } from '@/lib/migration/summary'
import { usePlanData } from '@/lib/migration/usePlan'
import type { MigrationScope } from '@/lib/migration/scope'
import { useCatalogueStore, visibleTechnologies } from '@/stores/catalogueStore'
import { stepState, usageFrom, vendorGates } from '@/lib/catalogue/engine'
import { CAPABILITY_LABEL } from '@/api/types/catalogue'
import { confidenceText } from '@/lib/catalogue/summary'
import { computeProjection } from '@/lib/readiness/projection'
import { transitionFor } from '@/api/types/readiness'
import { allObjectives } from '@/lib/indexes/registry'
import { useAuthStore } from '@/stores/authStore'
import type { GateId } from '@/stores/profileStore'

// ── Readiness module definition (Iteration 3 §1, Note 1) ────────────────────
// One nav list: the module tab strip and the drawer's Readiness sub-items both
// read `READINESS_FEATURES`; chips and counts are resolved once per page load
// by `useReadinessModule`.

// Iteration 5 §1–§2: Overview is Dashboard; each feature carries its drawer
// icon (Material Symbols, filled). Detail pages (objective, report, technology)
// follow their *origin* feature (§4, see nav.ts), not a fixed parent.
export type FeatureKey = 'dashboard' | 'progress' | 'submission' | 'tracker' | 'catalogue' | 'settings'

// Iteration 6 §15, Note 7: `coming_soon` items render disabled in the drawer
// with a `Coming soon` chip and never appear in the strip. Iteration 6 (POC)
// §2: `locked` applies only in the POC profile (stores/profileStore.ts); in
// Full every locked feature reads as available.
export type FeatureStatus = 'available' | 'coming_soon' | 'locked'

export interface FeatureDef {
  key: FeatureKey
  label: string
  route: string
  icon: string
  status: FeatureStatus
  // The POC feature gate behind this tab, if any (I6 POC §2).
  gate?: GateId
  // Pinned items sit at the bottom of the drawer, apart from the feature list.
  pinned?: boolean
  // Feature routes that keep this tab active on their own.
  match: (pathname: string) => boolean
}

// Order (I6 POC §4, both profiles): Dashboard · CSA Submission · Progress ·
// Migration Tracker · Technology Catalogue.
export const READINESS_FEATURES: FeatureDef[] = [
  { key: 'dashboard', label: 'Dashboard', route: '/readiness', icon: 'dashboard', status: 'available', match: (p) => p === '/readiness' || p === '/readiness/' || p.startsWith('/readiness/objectives') || p.startsWith('/readiness/scoring') },
  { key: 'submission', label: 'CSA Submission', route: '/readiness/submission', icon: 'assignment_turned_in', status: 'available', match: (p) => p.startsWith('/readiness/submission') },
  { key: 'progress', label: 'Progress', route: '/readiness/progress', icon: 'trending_up', status: 'available', gate: 'progress', match: (p) => p.startsWith('/readiness/progress') },
  { key: 'tracker', label: 'Migration Tracker', route: '/readiness/tracker', icon: 'alt_route', status: 'available', gate: 'migration_tracker', match: (p) => p.startsWith('/readiness/tracker') },
  { key: 'catalogue', label: 'Technology Catalogue', route: '/readiness/catalogue', icon: 'category', status: 'available', gate: 'technology_catalogue', match: (p) => p === '/readiness/catalogue' || p === '/readiness/catalogue/' },
  // Settings is pinned at the bottom of the drawer and disabled until it ships (I6 §15).
  { key: 'settings', label: 'Settings', route: '/readiness/settings', icon: 'settings', status: 'coming_soon', pinned: true, match: (p) => p.startsWith('/readiness/settings') },
]

// The strip and the drawer's main list: available, unpinned features only.
export const AVAILABLE_FEATURES = READINESS_FEATURES.filter((f) => f.status === 'available' && !f.pinned)
export const PINNED_FEATURES = READINESS_FEATURES.filter((f) => f.pinned)

const FEATURE_ROOTS = ['progress', 'submission', 'tracker', 'catalogue', 'objectives', 'scoring', 'report', 'export', 'assessments', 'snapshots', 'migration', 'settings']
export function isObjectiveRoute(pathname: string): boolean {
  const m = pathname.match(/^\/readiness\/([^/]+)\/?$/)
  return !!m && !FEATURE_ROOTS.includes(m[1])
}
export const objectivePath = (code: string) => `/readiness/${code}`

// The vendor behind an objective's gate (I5 §11: the `vendor-gated` flag tooltip
// and its link to the catalogue entry).
export interface GateDetail {
  technology_id: string
  technology: string
  vendor: string
  capability: string
  roadmap_date: string | null
  confidence: string
}

export function vendorGateDetails(liveTransitions: Record<string, string>, mscope: MigrationScope | null, usage: ReturnType<typeof usageFrom>['usage'], techs: ReturnType<typeof useCatalogueStore.getState>['technologies'], caps: ReturnType<typeof useCatalogueStore.getState>['capabilities']): Record<string, GateDetail> {
  const out: Record<string, GateDetail> = {}
  if (!mscope) return out
  mscope.items.forEach((i) =>
    i.step_links?.forEach((l) => {
      if (liveTransitions[l.objective_code] !== l.transition) return
      const st = stepState(l.objective_code, l.transition, l.bullet_index, mscope, usage, techs, caps)
      const g = [...st.gated].sort((a, b) => (b.roadmap_date ?? '').localeCompare(a.roadmap_date ?? ''))[0]
      if (!g) return
      const cur = out[l.objective_code]
      if (!cur || (g.roadmap_date ?? '') > (cur.roadmap_date ?? '')) out[l.objective_code] = { technology_id: g.technology.id, technology: g.technology.name, vendor: g.technology.vendor, capability: CAPABILITY_LABEL[g.capability], roadmap_date: g.roadmap_date, confidence: confidenceText(g.record?.roadmap_confidence) }
    })
  )
  return out
}

export interface FeatureChip {
  chip?: { text: string; tone: 'ok' | 'info' | 'warn' } | null
  count?: number | null
}

// Curators edit shared catalogue fields (§9.3).
export function useCanCurate(): boolean {
  const role = useAuthStore((s) => s.role)
  return (role ?? '').toLowerCase().includes('quantizant')
}

export function catalogueView(orgId: string, mscope: MigrationScope | null, techsAll: ReturnType<typeof useCatalogueStore.getState>['technologies'], caps: ReturnType<typeof useCatalogueStore.getState>['capabilities'], mappings: Record<string, string>) {
  const technologies = visibleTechnologies(techsAll, orgId)
  const { usage, unmatched } = usageFrom(orgId, mscope?.facts ?? null, technologies, mappings)
  return { technologies, capabilities: caps, usage, unmatched }
}

export function useReadinessModule(existing?: ReadinessCtx) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ctx = existing ?? useReadiness()
  const { orgId, indexId, index, scope } = ctx
  useEnsureMigrationScope(ctx)
  const mscope = useMigrationScope(orgId, indexId)
  const techsAll = useCatalogueStore((s) => s.technologies)
  const caps = useCatalogueStore((s) => s.capabilities)
  const mappingsAll = useCatalogueStore((s) => s.mappings)
  const mappings = useMemo(() => mappingsAll[orgId] ?? {}, [mappingsAll, orgId])
  const catalogue = useMemo(() => catalogueView(orgId, mscope, techsAll, caps, mappings), [orgId, mscope, techsAll, caps, mappings])
  const plan = usePlanData(orgId, indexId, !!index.submission)
  const summary = useMemo(() => (index.submission ? buildSummary(index, plan, mscope) : null), [index, plan, mscope])

  // Projection (§5) over the event log, with vendor gates on the next level
  // (§12: vendor dates from the catalogue feed the projection).
  const confirmedTransitions = useMemo(() => Object.fromEntries(allObjectives(index).map(({ objective }) => {
    const st = scope.states[objective.code]
    const lvl = !st || st.status === 'unset' || st.status === 'suggested' ? 'L0' : st.level
    return [objective.code, transitionFor(lvl)]
  })), [index, scope.states])
  const gates = useMemo(() => vendorGates(confirmedTransitions, mscope, catalogue.usage, catalogue.technologies, catalogue.capabilities), [confirmedTransitions, mscope, catalogue])
  const gateDetails = useMemo(() => vendorGateDetails(confirmedTransitions, mscope, catalogue.usage, catalogue.technologies, catalogue.capabilities), [confirmedTransitions, mscope, catalogue])
  const projection = useMemo(() => (index.scale === 'levels' ? computeProjection(index, scope.events ?? [], { gates }) : null), [index, scope.events, gates])

  const chips: Record<FeatureKey, FeatureChip> = {
    dashboard: {},
    progress: {},
    submission: { chip: plan.plan && index.submission ? (plan.plan.plan_state.status === 'submitted' ? { text: 'SUBMITTED', tone: 'ok' } : { text: `DRAFT v${plan.plan.plan_state.n}`, tone: 'info' }) : null },
    tracker: { count: mscope?.items.length ?? null },
    catalogue: { count: catalogue.technologies.length },
    settings: {},
  }

  return { ...ctx, mscope, plan, summary, catalogue, projection, gates, gateDetails, chips }
}

export type ModuleCtx = ReturnType<typeof useReadinessModule>

